import { formatDataTest } from '@/common/dataTest/DataTest';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useNavState } from '@/common/navState/NavStateContext';
import { IconUtils } from '@/common/utils/IconUtils';
import { StringUtils } from '@/common/utils/StringUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, CardActions, CardContent, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { Mode } from '../enumerations/Mode';
import { TextCardField } from './fields/TextCardField';

/**
 * EntityCard is a particular EntityObject's rendering within the EntityBoard framework
 * It is responsible for rendering the entity's card based on SlotSpecs and FieldDefs
 */
export const EntityCard = ({ entityObj, slotSpec, fieldToFieldDef, index }) => {

  const module = useModuleFromRoute();
  const { navigateTo } = useNavState();

  const navigateToEntity = () => {
    const route = module + '/' + StringUtils.toKebabConvention(slotSpec.entity);
    const navState = {
      entityId: entityObj.id,
      mode: Mode.DETAIL,
    }
    navigateTo(route, navState)
  }

  // Optional priority function should return a value between 1 (low priority), 10 (high priority), 11+ (overflow - leads to icon)
  const priorityObj = slotSpec.priority ? slotSpec.priority(entityObj) : 0;
  const bgColor = redHues[Math.min(priorityObj.index, 10) - 1];
  const statusEnumSpec = fieldToFieldDef.status.values.find(enumSpec => enumSpec.code === entityObj.status);
  const valStatusEnumSpec = fieldToFieldDef.valStatus.values.find(enumSpec => enumSpec.code === entityObj.valStatus);

  return (
    <Card sx={{ borderRadius: 4, boxShadow: 2, bgcolor: bgColor }}
    data-test-id={formatDataTest('board-card', slotSpec.title, 'index', index)}>

      {/* Main Content */}
      <CardContent>
        <Grid container spacing={0.25} direction="column">
          {slotSpec.fields.map(field => {
            const fieldDef = fieldToFieldDef[field];
            let CardFieldComponent = components[fieldDef.type] ?? TextCardField;
            return (
              <Grid item xs={6} key={field} data-test-id={formatDataTest('board-card-field', slotSpec.title, 'index', index, fieldDef.displayName)} >
                <Typography variant="subtitle2" color="textSecondary" align="left">{fieldDef.displayName}</Typography>
                <CardFieldComponent entityObj={entityObj} fieldDef={fieldDef} />
              </Grid>
            );
          })}
        </Grid>
      </CardContent>


      {/* Bottom Icons */}
      <CardActions sx={{ display: 'flex', justifyContent: 'flex-end', p: 0 }}>

        {/* Priority Overflow Icon */}
        {priorityObj != null && priorityObj.tooltip != null &&
          <Tooltip title={priorityObj.tooltip}>
            <FontAwesomeIcon
              data-test-id={formatDataTest('board-card-priority-icon', slotSpec.title, 'index', index)}
              icon={fas.faTriangleExclamation}
              color="red"
            />
          </Tooltip>
        }

        {/* Status Icon */}
        <FontAwesomeIcon
          data-test-id={formatDataTest('board-card-status', slotSpec.title, 'index', index)}
          icon={IconUtils.iconFromIconStr(statusEnumSpec.icon)}
          color={statusEnumSpec.color}
          spin={statusEnumSpec.icon.includes('spinner')}
        />

        {/* Status Icon */}
        <FontAwesomeIcon
          data-test-id={formatDataTest('entity-card-val-status', slotSpec.title, 'index', index)}
          icon={IconUtils.iconFromIconStr(valStatusEnumSpec.icon)}
          color={valStatusEnumSpec.color}
          spin={valStatusEnumSpec.icon.includes('spinner')}
        />

        {/* Hyperlink */}
        <IconButton data-test-id={formatDataTest('board-card-hyperlink', slotSpec.title, 'index', index)} size="small" onClick={navigateToEntity}>
          <FontAwesomeIcon icon={fas.faLink} />
        </IconButton>

      </CardActions>
    </Card>
  );
}

/*
* At compile time, prepare a map from FieldDefType to component
*/
// Vite replacement for require.context: eager loads all matching field renderer modules
const modules = import.meta.glob('./fields/*.js', { eager: true });
const components = Object.entries(modules).reduce((components, [key, module]) => {
  const componentName = key.replace('./fields/', '').replace('.js', '');
  const component = module[componentName];
  const componentKey = StringUtils.toCodeConvention(componentName.replace('CardField', ''));
  components[componentKey] = component;
  return components;
}, {});

/*
* Range of hues for the card priority
*/
const redHues = [
  '#FFFFFF',  // 1: pure white
  '#FFFBFB',  // 2
  '#FFF7F7',  // 3
  '#FFF3F3',  // 4
  '#FFEFEF',  // 5
  '#FFEBEB',  // 6
  '#FFE7E7',  // 7
  '#FFE3E3',  // 8
  '#FFDFDF',  // 9
  '#FFDBDB'   // 10: gentle rose
];